<template>
  <div>
    <div
      v-if="!canShowBaseLabel()"
      id="switch-field-label"
      :style="getLabelStyle()"
      :title="getLabel()"
    >
      {{ getLabel() }}
    </div>
    <v-switch
      v-model="computedValue"
      :color="config.color || '#65AD45'"
      :hide-details="config.hideDetails || 'auto'"
      :error-messages="getValidationErrors()"
      :error-count="getValidationErrors().length"
      :class="[getDisabled() ? 'switch-field--disabled' : 'switch-field']"
      :disabled="getDisabled()"
      @mousedown.stop.prevent="onMouseDown"
      @touchstart.stop.prevent="onSwitchClick"
    >
      <template
        v-if="getLabel() || showLoader"
        #label
      >
        <v-row>
          <v-col
            v-if="getLabel() && config.useBaseLabel"
            cols="auto"
          >
            {{ getLabel() }}
          </v-col>
          <v-col
            v-if="showLoaderComputed"
            cols="auto"
            class="mr-3"
            style="position: relative"
          >
            <v-overlay
              opacity="0"
              absolute
            >
              <v-progress-circular
                indeterminate
                :color="'var(--v-primary-base)'"
                size="20"
              />
            </v-overlay>
          </v-col>
        </v-row>
      </template>
    </v-switch>
  </div>
</template>

<script>
import { isEmpty, isObject } from 'lodash'

export default {
  name: 'SwitchField',

  props: {
    value: {
      type: [Boolean, Number],
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    customFieldKey: {
      type: String,
      default: ''
    },
    showLoader: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'onInput',
    'input',
    'change',
    'clear-validation-errors'
  ],

  data () {
    return {
      showLoaderComputed: false,
      internalValue: false
    }
  },

  computed: {
    computedValue: {
      get () {
        return this.internalValue
      },
      set (val) {
        this.internalValue = !!val
        this.$emit('input', val)
      }
    }
  },

  watch: {
    'config.setValue': {
      immediate: true,
      handler (val) {
        this.internalValue = !!val
      }
    },
    'config.showLoader': {
      immediate: true,
      handler (val) {
        this.showLoaderComputed = !!val
      }
    },
    showLoader: {
      immediate: true,
      handler (val) {
        this.showLoaderComputed = !!val
      }
    }
  },

  methods: {
    onMouseDown (event) {
      // Only left mouse click (button === 0)
      if (event?.button === 0) {
        this.onSwitchClick()
      }
    },

    async onSwitchClick () {
      const newValue = !this.internalValue
      const { onChange } = this.config || {}

      // Defensive emit cleanup
      this.$emit('clear-validation-errors')

      if (typeof onChange === 'function') {
        // Await external callback (can be async)
        await onChange({
          value: newValue,
          formData: this.formData
        })
        return
      }

      // Only reach here if allowed to toggle
      this.internalValue = newValue
      this.$emit('input', newValue)
      this.$emit('onInput', newValue)
      this.$emit('change', newValue)
    },

    onChange (val) {
      const { onChange } = this.config || {}

      let proceed = this.$emit('change', !!val)
      this.$emit('clear-validation-errors')

      if (typeof onChange === 'function') {
        proceed = onChange({
          value: val,
          formData: this.formData
        })

        if (proceed === false) {
          // without $nextTick, value is being changed, which we dont want here
          this.$nextTick(() => {
            this.internalValue = !val
          })
        }
      }
    },

    getValidationErrors () {
      let messages = []
      const { key } = this.config || {}

      if (!key || typeof key !== 'string' || isEmpty(this.validationErrors) || this.disabled) {
        return messages
      }

      for (const errorKey in this.validationErrors) {
        if (errorKey === key) {
          messages = this.validationErrors[errorKey]
        }
        else if (errorKey === this.customFieldKey) {
          messages = this.validationErrors[errorKey]
        }
      }

      return messages
    },

    getDisabled () {
      const { disabled } = this.config || {}
      return !!disabled || this.disabled
    },

    getLabelStyle () {
      const { labelStyle } = this.config || {}
      let style = {}

      if (labelStyle && isObject(labelStyle)) {
        style = {
          ...style,
          ...labelStyle
        }
      }

      return style
    },

    canShowBaseLabel () {
      const { useBaseLabel } = this.config || {}
      return useBaseLabel === true
    },

    getLabel () {
      const { label } = this.config || {}

      return label || ''
    }
  }
}
</script>

<style lang="scss" scoped>
#switch-field-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: 0;
  /* Never wrap - see TextField. The full text stays readable through the
     element's `title` on hover. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep {
  .switch-field {
    margin-top: unset !important;
  }

  .switch-field--disabled {
    margin-top: unset !important;
  }

  .switch-field--disabled > .v-input__control > .v-input__slot > label {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px;
    color: #6B7280;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: 0;
  }

  .switch-field > .v-input__control > .v-input__slot > label {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px;
    color: #111827;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0;
  }
}
</style>
